// src/App.js
import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/Common/ProtectedRoute';
import GuestRoute from './components/Common/GuestRoute';
import Navbar from './components/Common/UI/Navbar/Navbar.jsx';
import CookieConsent from './components/Common/UI/CookieConsent/CookieConsent.jsx';
import Loading from './components/Common/Loading.jsx';
import { AuthProvider } from './utils/AuthContext.jsx';
import { NotificationProvider } from './utils/NotificationContext.js';
import NotificationContainer from './components/Common/UI/Notification/NotificationContainer.jsx';
import ErrorBoundary from './components/Common/ErrorBoundary.jsx';

// --- Code Splitting: ленивая загрузка страниц ---
const Home = lazy(() => import('./pages/Home/Home.jsx'));
const Login = lazy(() => import('./pages/Login/Login'));
const Register = lazy(() => import('./pages/Register/Register'));
const Profile = lazy(() => import('./pages/Profile/Profile.jsx'));
const Projects = lazy(() => import('./pages/Projects/Projects.jsx'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound.jsx'));
const EmailConfirmation = lazy(() => import('./pages/EmailConfirmation/EmailConfirmation.jsx'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/ForgotPassword.jsx'));
const ResetPasswordConfirm = lazy(() => import('./pages/ResetPasswordConfirm/ResetPasswordConfirm.jsx'));
const ResendConfirmation = lazy(() => import('./pages/ResendConfirmation/ResendConfirmation.jsx'));
const Tracks = lazy(() => import('./pages/Tracks/Tracks.jsx'));
const CreateTrack = lazy(() => import('./pages/Tracks/CreateTrack/CreateTrack.jsx'));
const TrackDetails = lazy(() => import('./pages/Tracks/TrackDetails/TrackDetails.jsx'));
const Organizations = lazy(() => import('./pages/Organizations/Organizations.jsx'));
const CreateProject = lazy(() => import('./pages/Projects/CreateProject/CreateProject.jsx'));
const ProjectDetails = lazy(() => import('./pages/Projects/ProjectDetails/ProjectDetails.jsx'));
const News = lazy(() => import('./pages/News/News.jsx'));
const NewsDetail = lazy(() => import('./pages/News/NewsDetails/NewsDetails.jsx'));
const CreateNews = lazy(() => import('./pages/News/CreateNews/CreateNews.jsx'));
const RoleApplicationsList = lazy(() => import('./pages/Admin/RoleApplicationsList.jsx'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard.jsx'));
const VacancyDetails = lazy(() => import('./pages/Vacancies/VacancyDetails.jsx'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications.jsx'));
const StageRequestDetail = lazy(() => import('./pages/Projects/ProjectDetails/StageRequestDetail.jsx'));
const OrganizationDashboard = lazy(() => import('./pages/Organization/OrganizationDashboard.jsx'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy.jsx'));

function App() {
    return (
        <AuthProvider>
            <NotificationProvider>
                <Router>
                    <NotificationContainer />
                    <Navbar />
                    <CookieConsent />
                    <div className="App">
                        <main className="main-content">
                            <ErrorBoundary>
                                <Suspense fallback={<Loading />}>
                                    <Routes>
                                        {/* Публичные маршруты */}
                                        <Route path="/" element={<Home />} />

                                        {/* Только для гостей */}
                                        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
                                        <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
                                        <Route path="/confirm-email/:uid/:token" element={<GuestRoute><EmailConfirmation /></GuestRoute>} />
                                        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
                                        <Route path="/reset-password-confirm/:uid/:token" element={<GuestRoute><ResetPasswordConfirm /></GuestRoute>} />
                                        <Route path="/resend-confirmation" element={<GuestRoute><ResendConfirmation /></GuestRoute>} />

                                        {/* Публичные маршруты */}
                                        <Route path="/profile/:identifier?" element={<Profile />} />
                                        <Route path="/projects" element={<Projects />} />
                                        <Route path="/projects/:slug" element={<ProjectDetails />} />
                                        <Route path="/news" element={<News />} />
                                        <Route path="/news/:slug" element={<NewsDetail />} />
                                        <Route path="/vacancies/:slug" element={<VacancyDetails />} />
                                        <Route path="/organizations" element={<Organizations />} />
                                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                                        {/* Для авторизованных */}
                                        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                                        <Route path="/stage-verification/:slug" element={<ProtectedRoute><StageRequestDetail /></ProtectedRoute>} />
                                        <Route path="/tracks" element={<ProtectedRoute><Tracks /></ProtectedRoute>} />
                                        <Route path="/tracks/new" element={<ProtectedRoute><CreateTrack /></ProtectedRoute>} />
                                        <Route path="/tracks/:slug" element={<ProtectedRoute><TrackDetails /></ProtectedRoute>} />
                                        <Route path="/projects/new" element={<ProtectedRoute><CreateProject /></ProtectedRoute>} />
                                        <Route path="/news/create" element={<ProtectedRoute><CreateNews /></ProtectedRoute>} />

                                        {/* Только для admin/mentor */}
                                        <Route
                                            path="/admin"
                                            element={
                                                <ProtectedRoute allowedRoles={['admin', 'root']}>
                                                    <AdminDashboard />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/admin/role-applications"
                                            element={
                                                <ProtectedRoute allowedRoles={['admin', 'root']}>
                                                    <RoleApplicationsList />
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/organization/dashboard"
                                            element={
                                                <ProtectedRoute allowedRoles={['organization', 'admin', 'root']}>
                                                    <OrganizationDashboard />
                                                </ProtectedRoute>
                                            }
                                        />

                                        {/* 404 */}
                                        <Route path="*" element={<NotFound />} />
                                    </Routes>
                                </Suspense>
                            </ErrorBoundary>
                        </main>
                    </div>
                </Router>
            </NotificationProvider>
        </AuthProvider>
    );
}

export default App;